/**
 * Onboarding Policy — formal HR policy document, aligned with Employee Onboarding settings
 * (documents required, workflows, letters, applicability).
 */

import {
  MASTERS_DOCUMENT_COMPANY_ADDRESS,
  MASTERS_DOCUMENT_COMPANY_NAME,
} from "./masters/companyDocumentDefaults";

function escapeHtml(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function masterName(list, id) {
  if (!id) return null;
  const f = (list || []).find((x) => String(x.id) === String(id));
  return f?.name || null;
}

/** Hierarchical section numbers */
function createDocNumberer() {
  return {
    m: 0,
    s: 0,
    ss: 0,
    sss: 0,
    major() {
      this.m++;
      this.s = 0;
      this.ss = 0;
      this.sss = 0;
      return String(this.m);
    },
    sub() {
      this.s++;
      this.ss = 0;
      this.sss = 0;
      return `${this.m}.${this.s}`;
    },
    subsub() {
      this.ss++;
      this.sss = 0;
      return `${this.m}.${this.s}.${this.ss}`;
    },
    subsubsub() {
      this.sss++;
      return `${this.m}.${this.s}.${this.ss}.${this.sss}`;
    },
  };
}

function buildOnboardingApplicabilityProse(policy, options) {
  const org = escapeHtml(options.companyName || MASTERS_DOCUMENT_COMPANY_NAME);
  const p = policy || {};

  if (p.allEmployees) {
    const empOpts = options.employmentTypeOptions || [];
    const empTypes = Array.isArray(p.employmentTypes) ? p.employmentTypes : [];
    const empTypeLabels = empTypes.length
      ? empTypes.map((v) => empOpts.find((o) => o.value === v)?.label || v).filter(Boolean)
      : [];
    if (empTypeLabels.length) {
      return `<p>This onboarding policy applies to <strong>all new joiners</strong> of <strong>${org}</strong> whose employment type is one of: <strong>${empTypeLabels
        .map((x) => escapeHtml(x))
        .join(
          ", "
        )}</strong> (as reflected in HR records). Other employment types are not covered unless a separate policy applies.</p>
        <p>If you are unsure of your employment type, contact HR at <strong>${org}</strong>.</p>`;
    }
    return `<p>This onboarding policy applies to <strong>all new joiners</strong> of <strong>${org}</strong> across employment types, unless an individual exception is expressly documented by HR.</p>`;
  }

  const parts = [];
  const b = masterName(options.branchesList, p.branch);
  const d = masterName(options.departmentsList, p.department);
  const des = masterName(options.designationsList, p.designation);
  const g = masterName(options.gradesList, p.grade);
  const c = masterName(options.categoriesList, p.category);
  const sd = masterName(options.subDepartmentsList, p.subDepartment);
  const loc = (options.locationOptions || []).find((o) => o.value === (p.location || ""))?.label;
  const ind = String(p.individualEmployeeIds || "").trim();
  const empOpts = options.employmentTypeOptions || [];
  const empTypes = Array.isArray(p.employmentTypes) ? p.employmentTypes : [];
  const empTypeLabels = empTypes.length
    ? empTypes
        .map((v) => empOpts.find((o) => o.value === v)?.label || v)
        .filter(Boolean)
        .join(", ")
    : "";

  parts.push(
    `<p>The rules in this document apply to new joiners of <strong>${org}</strong> who fall within the coverage below. Assignment follows the criteria below and is administered by HR.</p>`
  );

  const criteria = [];
  if (b) criteria.push(`<strong>Branch / unit:</strong> ${escapeHtml(b)}`);
  if (d) criteria.push(`<strong>Department:</strong> ${escapeHtml(d)}`);
  if (des) criteria.push(`<strong>Designation / role family:</strong> ${escapeHtml(des)}`);
  if (g) criteria.push(`<strong>Grade / level:</strong> ${escapeHtml(g)}`);
  if (c) criteria.push(`<strong>Employee category:</strong> ${escapeHtml(c)}`);
  if (sd) criteria.push(`<strong>Sub-department / division:</strong> ${escapeHtml(sd)}`);
  if (loc && loc !== "All Locations") criteria.push(`<strong>Location:</strong> ${escapeHtml(loc)}`);
  if (empTypeLabels) {
    criteria.push(`<strong>Employment type(s):</strong> ${escapeHtml(empTypeLabels)}`);
  }

  if (criteria.length) {
    parts.push(
      `<p>Where the following filters are set, the policy covers joiners who match <em>all</em> of the applicable selections:</p><ul>${criteria.map((x) => `<li>${x}</li>`).join("")}</ul>`
    );
  } else {
    parts.push(
      `<p>No specific branch, department, or other structural filters are set out above for this policy. Coverage follows the standard assignment practice established by HR for <strong>${org}</strong>.</p>`
    );
  }

  if (ind) {
    parts.push(
      `<p><strong>Named individuals:</strong> The policy may also be assigned to specific employee records: ${escapeHtml(ind)}.</p>`
    );
  }

  parts.push(`<p>If you are unsure whether this policy applies to you, contact HR at <strong>${org}</strong>.</p>`);

  return parts.join("");
}

function letterLabel(opts, value) {
  if (!value) return "As per HR template selection";
  const o = (opts || []).find((x) => x.value === value);
  return o?.label || value;
}

const DOC_CHECKLIST_LABELS = {
  requireIdProof: "Government ID proof",
  requireBankDetails: "Bank account details",
  requireEducationalDocs: "Educational certificates",
  requirePreviousEmployment: "Previous employment proof",
  requirePhoto: "Photograph",
  requirePolicyAck: "Policy acknowledgment",
};

/**
 * @param {object} policy
 * @param {object} options — same shape as confirmation policy document options where applicable
 */
export function buildOnboardingPolicyDocumentHtml(policy, options = {}) {
  const p = policy || {};
  const org = options.companyName || MASTERS_DOCUMENT_COMPANY_NAME;
  const companyName = escapeHtml(org);
  const companyAddress = options.companyAddress ?? MASTERS_DOCUMENT_COMPANY_ADDRESS;
  const preparedBy = options.preparedBy || "Human Resources";
  const approvedBy = options.approvedBy || "Management";
  const roleLabel =
    options.workflowRoleLabel ||
    ((role) => {
      const map = {
        manager: "Manager",
        hr: "HR",
        management: "Management",
        reporting_manager: "Reporting Manager",
      };
      return map[role] || role || "—";
    });

  const policyTitle = escapeHtml(p.policyName || p.name || "HR Onboarding Policy");
  const ver = p.policyVersion || 1;
  const effective = escapeHtml(p.effectiveDate || "—");
  const pushedNote =
    p.policyDocumentPushed && p.policyPushedAt
      ? `<p class="meta"><strong>Employee publication:</strong> This version was published to covered joiners on ${escapeHtml(
          new Date(p.policyPushedAt).toLocaleString()
        )}.</p>`
      : "";

  const wf = Array.isArray(p.workflowSteps) ? p.workflowSteps : [];
  const cancelWf = Array.isArray(p.cancellationWorkflowSteps) ? p.cancellationWorkflowSteps : [];
  const checklist = Array.isArray(p.checklistMappings) ? p.checklistMappings : [];

  const approvalMethodProse =
    p.approvalMethod === "workflow"
      ? "Onboarding requests follow the multi-step approval workflow set out in this policy. Each step must be completed within the service levels described below."
      : p.approvalMethod === "on_instance"
      ? "Approvals may be initiated on a case-by-case basis. Where workflow steps apply to an individual case, the same step order and timelines apply."
      : "Eligible onboarding cases may be progressed automatically when the rules defined in this policy are satisfied, without a manual approval chain. HR may still review exceptions.";

  const docNum = createDocNumberer();

  const wfStepsHtml = (steps, titleWithoutNumber) => {
    const sec = docNum.sub();
    const fullTitle = `${sec} ${titleWithoutNumber}`;
    if (!steps.length) {
      return `<h3>${escapeHtml(fullTitle)}</h3><p>For this path, no separate approval steps are defined in the current version of this policy. HR will apply the general approval approach described above.</p>`;
    }
    const items = steps
      .map((s) => {
        const stepNum = docNum.subsub();
        const detailNum = docNum.subsubsub();
        const r = roleLabel(s.role);
        const line = s.notifyOnly
          ? `This step is for <strong>notification only</strong> (no approval action required from ${escapeHtml(r)}).`
          : `${escapeHtml(r)} must complete the approval action within <strong>${s.slaDays ?? 3} calendar day(s)</strong> unless escalated.`;
        return `<li><p><strong>${stepNum} — ${escapeHtml(r)}.</strong></p><p class="step-detail"><strong>${detailNum}</strong> ${line}</p></li>`;
      })
      .join("");
    return `<h3>${escapeHtml(fullTitle)}</h3><ol class="steps">${items}</ol>`;
  };

  const requiredDocs = Object.entries(DOC_CHECKLIST_LABELS)
    .filter(([key]) => p[key])
    .map(([, label]) => label);

  const renderChecklistSection = () => {
    const sec = docNum.major();
    if (checklist.length) {
      const rows = checklist.map((item) => {
        const rowNum = docNum.sub();
        const trig =
          {
            onboarding_request: "Onboarding request",
            document_verification: "Document verification",
            onboarding_approval: "Onboarding approval",
            cancellation: "Cancellation / withdrawal",
            confirmation: "Confirmation approval",
            extension: "Extension approval",
            termination: "Termination approval",
          }[item.triggerEvent] || item.triggerEvent || "—";
        const dept = item.department ? escapeHtml(item.department) : "—";
        const due =
          item.dueDateLogic === "approval_date"
            ? "from the approval date"
            : item.dueDateLogic === "fixed_days"
            ? `within ${item.dueDateDays ?? 7} day(s) from trigger`
            : escapeHtml(item.dueDateLogic || "—");
        return `<tr>
          <td>${rowNum}</td>
          <td>${escapeHtml(trig)}</td>
          <td>${dept}</td>
          <td>${escapeHtml(item.name || "—")}</td>
          <td>${item.mandatory ? "Mandatory" : "Optional"}</td>
          <td>${due}</td>
        </tr>`;
      });
      return `
      <h2>${sec}. Checklist and compliance tasks</h2>
      <p>The following tasks may be assigned to teams when the stated event occurs. Complete mandatory items on time so that onboarding is not delayed.</p>
      <table class="grid">
        <thead>
          <tr><th>Ref.</th><th>When</th><th>Department</th><th>Task</th><th>Nature</th><th>Due rule</th></tr>
        </thead>
        <tbody>${rows.join("")}</tbody>
      </table>
    `;
    }
    return `<h2>${sec}. Checklist and compliance tasks</h2><p>No additional checklist tasks are listed in this version of the policy. HR may still assign tasks under general procedures.</p>`;
  };

  const addressBlock = companyAddress
    ? `<p class="addr">${escapeHtml(companyAddress).replace(/\n/g, "<br/>")}</p>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${policyTitle} — ${companyName}</title>
  <style>
    body { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12.5px; line-height: 1.6; color: #1e293b; max-width: 820px; margin: 0 auto; padding: 32px; }
    .letterhead { border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px; }
    .company { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .doc-title { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 15px; font-weight: 700; color: #0f172a; margin: 16px 0 8px 0; text-transform: uppercase; letter-spacing: .06em; }
    .addr { margin: 0; color: #475569; font-size: 11px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .control { margin-top: 14px; padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 11px; color: #334155; }
    .control strong { color: #0f172a; }
    p.meta { font-size: 11px; color: #64748b; margin-top: 10px; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    h2 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 13px; margin: 22px 0 10px 0; color: #0f172a; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    h3 { font-family: "Segoe UI", Inter, Arial, sans-serif; font-size: 12px; margin: 14px 0 8px 0; color: #334155; }
    p { margin: 0 0 12px 0; text-align: justify; }
    ul { margin: 8px 0 12px 20px; }
    li { margin-bottom: 6px; }
    ol.steps { margin: 8px 0 16px 0; padding: 0; list-style-type: none; }
    ol.steps li { margin-bottom: 10px; }
    table.grid { width: 100%; border-collapse: collapse; font-size: 11px; margin: 10px 0 16px 0; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    table.grid th, table.grid td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; vertical-align: top; }
    table.grid th { background: #f1f5f9; font-weight: 600; color: #334155; }
    .sign { margin-top: 32px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; font-size: 11px; color: #475569; font-family: "Segoe UI", Inter, Arial, sans-serif; }
    .sign .line { border-top: 1px solid #94a3b8; padding-top: 8px; margin-top: 36px; color: #0f172a; font-weight: 600; }
    @media print { body { padding: 18px; } }
  </style>
</head>
<body>
  <div class="letterhead">
    <p class="company">${companyName}</p>
    ${addressBlock}
    <p class="doc-title">HR onboarding policy</p>
    <p style='font-family:"Segoe UI",Inter,Arial,sans-serif;font-size:13px;margin:0 0 4px 0;'><strong>${policyTitle}</strong></p>
    <div class="control">
      <strong>Document version:</strong> ${escapeHtml(String(ver))} &nbsp;|&nbsp;
      <strong>Effective from:</strong> ${effective} &nbsp;|&nbsp;
      <strong>Owned by:</strong> ${escapeHtml(preparedBy)} / ${escapeHtml(approvedBy)}
    </div>
    ${pushedNote}
  </div>

  <h2>${docNum.major()}. Purpose</h2>
  <p><strong>${docNum.sub()}.</strong> This policy explains how <strong>${companyName}</strong> manages <strong>employee onboarding</strong>: collecting information and documents before and after joining, routing approvals where required, issuing welcome or onboarding communications, and coordinating tasks so new joiners can start productively and in line with company standards.</p>
  <p><strong>${docNum.sub()}.</strong> It operates together with your employment contract, offer letter, and other HR policies. Where this document refers to reminders, workflows, or letters, the arrangements approved by HR apply; this text describes the intended experience for new joiners and people managers.</p>

  <h2>${docNum.major()}. Applicability</h2>
  ${buildOnboardingApplicabilityProse(p, { ...options, companyName: org })}
  <h3>${docNum.sub()}. What joiners and managers are expected to do</h3>
  <p>Joiners should submit required information and documents within the timelines communicated by HR. Managers should complete review and approval steps on time. HR may coordinate with IT, Admin, and Finance for access, assets, and payroll setup as applicable.</p>

  <h2>${docNum.major()}. Definitions</h2>
  <p><strong>${docNum.sub()}. Onboarding</strong> means the process from offer acceptance (or hire decision) through joining day and initial completion of mandatory tasks, including document collection, verification, and role setup.</p>
  <p><strong>${docNum.sub()}. Onboarding request</strong> means a formal request or case record that tracks a new joiner through the steps defined in this policy.</p>
  <p><strong>${docNum.sub()}. Cancellation</strong> means withdrawal or closure of an onboarding case before completion, subject to the cancellation workflow where applicable.</p>

  <h2>${docNum.major()}. Documents and information required</h2>
  <p><strong>${docNum.sub()}.</strong> Depending on how this policy is set, HR may require the following categories of documents or confirmations before onboarding is completed:</p>
  <ul>
    ${
      requiredDocs.length
        ? requiredDocs.map((label) => `<li><strong>${escapeHtml(label)}</strong></li>`).join("")
        : `<li><em>No specific document categories are marked as mandatory in this version of the policy; HR will communicate requirements by case.</em></li>`
    }
  </ul>
  <p><strong>${docNum.sub()}.</strong> Reminders related to joining may be sent <strong>${escapeHtml(
    String(p.reminderDays ?? "—")
  )}</strong> calendar day(s) before the <strong>date of joining</strong> when workflow-based onboarding is used.</p>

  <h2>${docNum.major()}. How approvals work</h2>
  <p><strong>${docNum.sub()}.</strong> ${approvalMethodProse}</p>
  <p><strong>${docNum.sub()}.</strong> Where parallel approvers are used, multiple roles may act as defined for this policy. Escalation may apply if no action is taken within <strong>${escapeHtml(
    String(p.workflowEscalationAfterDays ?? 3)
  )}</strong> day(s).</p>
  <p><strong>${docNum.sub()}.</strong> ${
    p.workflowRejectionWithReason !== false
      ? "If a request is rejected, a <strong>reason</strong> should be recorded."
      : "Rejection reasons may be recorded as per practice."
  } ${
    p.workflowReinitiationAllowed !== false
      ? "A fresh request may be allowed after rejection, as per HR guidance."
      : "Re-initiation after rejection may be restricted; contact HR."
  }</p>
  ${wfStepsHtml(wf, "Onboarding approval workflow")}
  ${
    cancelWf.length
      ? wfStepsHtml(cancelWf, "Cancellation or withdrawal of onboarding")
      : ""
  }

  ${renderChecklistSection()}

  <h2>${docNum.major()}. Letters and communications</h2>
  <p><strong>${docNum.sub()}.</strong> ${
    p.autoLetter === "yes"
      ? `Onboarding or welcome communications may be issued <strong>automatically</strong> when eligibility criteria under this policy are met.`
      : `Onboarding letters are not issued automatically; HR will issue communications as appropriate.`
  }</p>
  <p><strong>${docNum.sub()}.</strong> Where a template is selected for standard onboarding letters: <strong>${escapeHtml(
    letterLabel(options.letterFormatOptions, p.letterFormat)
  )}</strong>.</p>
  <p><strong>${docNum.sub()}.</strong> ${
    p.useCancellationLetter
      ? `Cancellation communications may use: <strong>${escapeHtml(
          letterLabel(options.letterFormatOptions, p.cancellationLetterFormat)
        )}</strong>.`
      : `Cancellation letter templates are not selected for this policy; HR will handle any cancellation communication as appropriate.`
  }</p>
  <p><strong>${docNum.sub()}.</strong> Official channels may include email, the employee portal, or physical letter as per practice.</p>

  <h2>${docNum.major()}. Disclaimer</h2>
  <p>This document is an employee-facing summary of onboarding rules approved for <strong>${companyName}</strong>. It is not a contract by itself. Your employment contract, offer letter, and applicable law may contain additional provisions — where there is a conflict, those documents and the law prevail.</p>
  <p>${companyName} may change this policy from time to time. When a new version applies, HR will communicate material changes. For questions, contact HR.</p>

  <div class="sign">
    <div>
      <div class="line">Prepared by (${escapeHtml(preparedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
    <div>
      <div class="line">Approved by (${escapeHtml(approvedBy)})</div>
      <span>Name &amp; signature</span>
    </div>
  </div>
</body>
</html>`;
}

export function printOnboardingPolicyDocumentHtml(htmlString) {
  const w = window.open("", "_blank");
  if (!w) {
    window.print();
    return;
  }
  w.document.write(htmlString);
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
}
